/**
 * Terms of Service Page
 * Noodle Studio
 */

import React from 'react';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { ArrowLeft, ArrowRight, Shield, FileText, Scale, AlertTriangle, Users, Globe, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';

interface TermsPageProps {
  onBack: () => void;
  onPrivacy: () => void;
  onAbout?: () => void;
}

export const TermsPage: React.FC<TermsPageProps> = ({ onBack, onPrivacy, onAbout }) => {
  usePageMeta(PAGE_META.terms);
  const lastUpdated = 'February 4, 2026';

  return (
    <div className="fixed inset-0 z-[200] bg-cream overflow-auto">
      {/* The bar every page has: the way back, and whose page this is. The page's name heads the content. */}
      <header className="ns-pagebar">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button onClick={onBack} className="ns-btn ns-btn-ghost ns-btn-sm shrink-0">
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>
          <Link to="/" className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio home">
            <BrandMark size={32} />
            <span className="text-[1.0625rem] font-bold tracking-[-0.02em] text-ink">Noodle Studio</span>
            <BetaTag />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="ns-card p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="ns-mark ns-mark-lavender">
                <FileText aria-hidden="true" />
              </span>
              <div>
                <h1 className="ns-t-page">Terms of Service</h1>
                <p className="text-ink-soft text-sm">Last updated: {lastUpdated}</p>
              </div>
            </div>
            <p className="text-ink-soft">
              Welcome to Noodle Studio. By accessing or using our service, 
              you agree to be bound by these Terms of Service. Please read them carefully.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {/* Section 1: Acceptance */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">1. Acceptance of Terms</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>
                  By creating an account, accessing, or using Noodle Studio ("Service"), you agree to be bound by 
                  these Terms of Service ("Terms"), our Privacy Policy, and all applicable laws and regulations. If you 
                  do not agree with any part of these terms, you may not use our Service.
                </p>
                <p>
                  Noodle Studio ("Company", "we", "us", or "our") reserves the right to update these Terms at any 
                  time. We will notify users of significant changes via email or in-app notification. Your continued use 
                  of the Service after changes constitutes acceptance of the updated Terms.
                </p>
              </div>
            </section>

            {/* Section 2: Eligibility */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">2. Eligibility & Account Registration</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p><strong className="text-ink">Age Requirement:</strong> You must be at least 13 years old to use our Service. If you are under 18, you must have parental or guardian consent.</p>
                <p><strong className="text-ink">Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
                <p><strong className="text-ink">Accurate Information:</strong> You agree to provide accurate, current, and complete information during registration and to update it as necessary.</p>
                <p><strong className="text-ink">One Account Per User:</strong> Each user may maintain only one account unless explicitly authorized by Noodle Studio.</p>
              </div>
            </section>

            {/* Section 3: Service Description */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">3. Service Description</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>
                  Noodle Studio is a visual workspace platform that enables users to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Generate AI-powered images and videos using various AI providers</li>
                  <li>Create and organize content on a visual canvas workspace</li>
                  <li>Store and sync projects across devices via cloud storage</li>
                  <li>Connect and share content to social media platforms</li>
                  <li>Collaborate on creative projects</li>
                </ul>
                <p>
                  The Service may integrate with third-party AI providers (OpenAI, Google, etc.). 
                  Your use of AI features is also subject to those providers' terms of service.
                </p>
              </div>
            </section>

            {/* Section 4: Acceptable Use */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">4. Acceptable Use Policy</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>You agree NOT to use the Service to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Generate, upload, or distribute illegal, harmful, threatening, abusive, defamatory, or obscene content</li>
                  <li>Create content that infringes on intellectual property rights of others</li>
                  <li>Generate deepfakes or non-consensual intimate imagery of real persons</li>
                  <li>Impersonate any person or entity, or falsely claim affiliation</li>
                  <li>Distribute malware, viruses, or harmful code</li>
                  <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                  <li>Use automated systems (bots, scrapers) without explicit permission</li>
                  <li>Circumvent usage limits or abuse free tier offerings</li>
                  <li>Use the Service for any illegal purpose or in violation of any laws</li>
                </ul>
                <p>
                  We reserve the right to remove content and suspend or terminate accounts that violate these policies.
                </p>
              </div>
            </section>

            {/* Section 5: AI Content & Ownership */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">5. AI-Generated Content & Ownership</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p><strong className="text-ink">Your Content:</strong> You retain ownership of original content you upload to the Service (sketches, text, images you create).</p>
                <p><strong className="text-ink">AI-Generated Content:</strong> Content generated by AI using our Service is subject to the terms of the underlying AI provider. Generally:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>You may use AI-generated content for personal and commercial purposes</li>
                  <li>You are responsible for ensuring your use complies with applicable laws</li>
                  <li>AI-generated content may not be eligible for copyright protection in all jurisdictions</li>
                </ul>
                <p><strong className="text-ink">License to Us:</strong> By uploading content, you grant Noodle Studio a non-exclusive, worldwide license to store, display, and process your content as necessary to provide the Service.</p>
                <p><strong className="text-ink">No Training:</strong> We do not use your personal content to train AI models without explicit consent.</p>
              </div>
            </section>

            {/* Section 6: Subscription & Payments */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">6. Subscription & Payments</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p><strong className="text-ink">Free Tier:</strong> We offer a free tier with limited features and usage quotas.</p>
                <p><strong className="text-ink">Paid Plans:</strong> Premium features are available through paid subscription plans. Pricing is displayed at purchase time.</p>
                <p><strong className="text-ink">Billing:</strong> Subscriptions are billed in advance on a monthly or annual basis. Payments are processed through our payment providers (Solana, Stripe).</p>
                <p><strong className="text-ink">Refunds:</strong> Refunds are provided at our discretion. Generally, we do not offer refunds for partial subscription periods.</p>
                <p><strong className="text-ink">Price Changes:</strong> We may change pricing with 30 days notice. Existing subscriptions will be honored until renewal.</p>
              </div>
            </section>

            {/* Section 7: Disclaimers */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">7. Disclaimers & Limitations</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p><strong className="text-ink">AS-IS Service:</strong> The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied.</p>
                <p><strong className="text-ink">No Guarantee:</strong> We do not guarantee that the Service will be uninterrupted, error-free, or secure.</p>
                <p><strong className="text-ink">AI Limitations:</strong> AI-generated content may contain errors, inaccuracies, or inappropriate material. You are responsible for reviewing AI outputs before use.</p>
                <p><strong className="text-ink">Third-Party Services:</strong> We are not responsible for third-party services (AI providers, social media platforms) integrated with our Service.</p>
                <p><strong className="text-ink">Limitation of Liability:</strong> To the maximum extent permitted by law, Noodle Studio's total liability shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
              </div>
            </section>

            {/* Section 8: Termination */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">8. Termination</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p><strong className="text-ink">By You:</strong> You may terminate your account at any time through account settings or by contacting support.</p>
                <p><strong className="text-ink">By Us:</strong> We may suspend or terminate your account for violations of these Terms, illegal activity, or at our discretion with notice.</p>
                <p><strong className="text-ink">Effect of Termination:</strong> Upon termination, your right to use the Service ceases. We may retain certain data as required by law or for legitimate business purposes.</p>
                <p><strong className="text-ink">Data Export:</strong> You may request export of your data before termination. We will make reasonable efforts to provide this within 30 days.</p>
              </div>
            </section>

            {/* Section 9: Governing Law */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">9. Governing Law & Disputes</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, 
                  United States, without regard to conflict of law principles.
                </p>
                <p>
                  Any disputes arising from these Terms or the Service shall first be attempted to be resolved through 
                  good-faith negotiation. If unresolved, disputes shall be submitted to binding arbitration in Delaware.
                </p>
                <p>
                  You agree to waive any right to participate in class action lawsuits against Noodle Studio.
                </p>
              </div>
            </section>

            {/* Section 10: Contact */}
            <section className="ns-card">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-5 h-5 shrink-0 text-lavender-deep" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-ink">10. Contact Us</h2>
              </div>
              <div className="text-ink-soft space-y-3">
                <p>If you have questions about these Terms:</p>
                <div className="ns-well p-4 space-y-2">
                  <p><strong className="text-ink">Noodle Studio</strong></p>
                  <p>Write to us on the <Link to="/bugs" className="ns-link">Known issues</Link> page, signed in, and say it is about the Terms. It is read by the people who run Noodle Studio and is not published until reviewed.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Related Links */}
          <div className="ns-card mt-8">
            <h2 className="text-lg font-semibold text-ink mb-4">Related documents</h2>
            <div className="flex flex-wrap gap-3">
              <Link to="/privacy" className="ns-btn ns-btn-soft">
                Privacy Policy
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
          <nav aria-label="Footer" className="flex items-center gap-1">
            <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
            <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};
